import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import axios from "axios";
import { defaultSiteCopy, SiteCopy } from "../utils/siteCopy";

const FooterContainer = styled.footer`
    background: #f8f9fa;
    padding: 3rem 0 2rem;
    margin-top: 4rem;
    border-top: 1px solid #e9ecef;
`;

const FooterContent = styled.div`
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;

    @media (max-width: 768px) {
        padding: 0 15px;
        gap: 1.5rem;
    }
`;

const SocialLinks = styled.div`
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;
`;

const SocialButton = styled.a`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: #666;
    color: white;
    border-radius: 50%;
    text-decoration: none;
    transition: all 0.3s ease;
    font-size: 1.2rem;

    &:hover {
        background: #333;
        transform: translateY(-2px);
    }

    @media (max-width: 768px) {
        width: 35px;
        height: 35px;
        font-size: 1rem;
    }
`;

const CustomText = styled.div`
    text-align: center;
    margin-bottom: 1.5rem;
`;

const MadeByText = styled.p`
    font-weight: bold;
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
    color: #333;

    @media (max-width: 768px) {
        font-size: 1rem;
    }
`;

const TaglineText = styled.p`
    font-size: 0.9rem;
    color: #666;
    margin: 0;

    @media (max-width: 768px) {
        font-size: 0.8rem;
    }
`;

const FooterNav = styled.nav`
    display: flex;
    gap: 2rem;
    align-items: center;
    flex-wrap: wrap;
    justify-content: center;

    @media (max-width: 768px) {
        gap: 1.5rem;
        flex-direction: column;
    }
`;

const FooterNavLink = styled(Link)`
    color: #666;
    text-decoration: none;
    font-size: 0.9rem;
    transition: color 0.3s ease;

    &:hover {
        color: #333;
    }

    @media (max-width: 768px) {
        font-size: 0.8rem;
    }
`;

const AdminLink = styled(Link)`
    color: #667eea;
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 500;
    transition: color 0.3s ease;

    &:hover {
        color: #5a6fd8;
    }

    @media (max-width: 768px) {
        font-size: 0.8rem;
    }
`;

const Divider = styled.div`
    width: 1px;
    height: 16px;
    background: #ddd;
    margin: 0 1rem;

    @media (max-width: 768px) {
        display: none;
    }
`;

const Footer: React.FC = () => {
    const [siteCopy, setSiteCopy] = useState<SiteCopy>(defaultSiteCopy);

    useEffect(() => {
        const fetchSiteCopy = async () => {
            try {
                const response = await axios.get("/api/site-copy");
                setSiteCopy({ ...defaultSiteCopy, ...(response.data.data || {}) });
            } catch (error) {
                console.error("Error fetching footer copy:", error);
            }
        };

        fetchSiteCopy();
    }, []);

    return (
        <FooterContainer>
            <FooterContent>
                <SocialLinks>
                    <SocialButton href={siteCopy.footer_naver_url || defaultSiteCopy.footer_naver_url} target="_blank" rel="noopener noreferrer" aria-label="Naver">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M16.273 12.845L7.376 0H0v24h7.727V11.155L16.624 24H24V0h-7.727v12.845z" />
                        </svg>
                    </SocialButton>
                </SocialLinks>

                <CustomText>
                    <MadeByText>{siteCopy.footer_brand}</MadeByText>
                    <TaglineText>{siteCopy.footer_tagline}</TaglineText>
                </CustomText>

                <FooterNav>
                    <FooterNavLink to="/terms">{siteCopy.footer_terms_label}</FooterNavLink>
                    <Divider />
                    <FooterNavLink to="/privacy">{siteCopy.footer_privacy_label}</FooterNavLink>
                    <Divider />
                    <AdminLink to="/admin">{siteCopy.footer_admin_label}</AdminLink>
                </FooterNav>
            </FooterContent>
        </FooterContainer>
    );
};

export default Footer;
